import { useState, useEffect } from 'react';
import { settingsService } from '../services/settingsService';

/**
 * Hook som leser innstillingen 'countingDashboard.autoRefreshEnabled' (36334500b).
 *
 * Styrer om auto-oppdateringen av aktive tellinger i telledashbordet starter PÅ eller
 * AV som standard når skjermen åpnes. Lagersjefen kan fortsatt slå den på/av manuelt —
 * innstillingen styrer kun start-tilstanden. Nyttig for storskjerm-/dashboardvisning.
 *
 * Oppløsning følger hierarkiet Profile -> System -> Default via getEffectiveSetting
 * (SettingDefinition tillater både System- og Profile-nivå, migrasjon 088).
 *
 * Returnerer { enabled, loaded } slik at forelderen kan bruke `loaded` til å sette
 * start-tilstanden nøyaktig én gang (uten å overstyre senere manuelle valg).
 *
 * Default: false (av med mindre tenant/bruker eksplisitt slår den på).
 */
export function useCountingDashboardAutoRefresh(): { enabled: boolean; loaded: boolean } {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;

    const loadSetting = async () => {
      try {
        const setting = await settingsService.getEffectiveSetting('countingDashboard.autoRefreshEnabled');
        if (!cancelled) {
          if (setting && setting.currentValue !== null && setting.currentValue !== undefined) {
            const val = setting.currentValue.toString().toLowerCase();
            setEnabled(val === 'true' || val === '1' || val === 'ja');
          } else {
            setEnabled(false);
          }
        }
      } catch (err) {
        console.warn('Could not load countingDashboard.autoRefreshEnabled setting, defaulting to false:', err);
        if (!cancelled) {
          setEnabled(false);
        }
      } finally {
        if (!cancelled) {
          setLoaded(true);
        }
      }
    };

    loadSetting();

    return () => { cancelled = true; };
  }, []);

  return { enabled, loaded };
}
